// components/RecordingStudio/ProjectSettings.jsx
import React, { useState } from "react";
import { X, Settings2, Zap } from "lucide-react";

const ProjectSettings = ({ project, supportedFormats, onSave, onClose }) => {
  const [format, setFormat] = useState(project?.outputFormat || "mp3");
  const [sampleRate, setSampleRate] = useState(project?.sampleRate || 24000);
  const currentFormat = supportedFormats.find((f) => f.format === format);

  const formatDesc = {
    mp3: "Best for streaming, sharing & general use",
    wav: "Highest quality for editing & production",
    ogg: "Great for web & game audio",
    mulaw: "Optimised for telephony systems",
  };

  const qualityLevel =
    sampleRate >= 44100
      ? "Ultra"
      : sampleRate >= 24000
        ? "High"
        : sampleRate >= 16000
          ? "Medium"
          : "Low";

  const qualityColor =
    sampleRate >= 44100
      ? "#34d399"
      : sampleRate >= 24000
        ? "#60a5fa"
        : sampleRate >= 16000
          ? "#fbbf24"
          : "#f87171";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.7)", backdropFilter: "blur(12px)" }}
    >
      <div
        className="w-full max-w-md rounded-3xl overflow-hidden shadow-2xl"
        style={{
          background: "linear-gradient(180deg, #0f1117 0%, #0a0c12 100%)",
          border: "1px solid rgba(255,255,255,0.07)",
          boxShadow: "0 32px 80px rgba(0,0,0,0.7)",
        }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-6 py-5"
          style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}
        >
          <div className="flex items-center gap-3">
            <div
              className="w-9 h-9 rounded-xl flex items-center justify-center"
              style={{
                background: "linear-gradient(135deg, #4f46e5, #7c3aed)",
              }}
            >
              <Settings2 size={16} className="text-white" />
            </div>
            <h2 className="text-lg font-bold text-white tracking-tight">
              Project Settings
            </h2>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-xl text-gray-600 hover:text-white transition-colors"
            style={{
              background: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.07)",
            }}
          >
            <X size={15} />
          </button>
        </div>

        <div className="px-6 py-6 space-y-5">
          {/* Format */}
          <div className="space-y-2">
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-widest">
              Output Format
            </label>
            <div className="grid grid-cols-2 gap-2">
              {supportedFormats.map((f) => (
                <button
                  key={f.format}
                  onClick={() => {
                    setFormat(f.format);
                    setSampleRate(f.defaultRate || 24000);
                  }}
                  className="relative px-4 py-3 rounded-xl text-left transition-all"
                  style={{
                    background:
                      format === f.format
                        ? "rgba(139,92,246,0.15)"
                        : "rgba(255,255,255,0.04)",
                    border:
                      format === f.format
                        ? "1px solid rgba(139,92,246,0.5)"
                        : "1px solid rgba(255,255,255,0.07)",
                  }}
                >
                  <div className="flex items-center justify-between mb-0.5">
                    <span
                      className="text-sm font-bold uppercase"
                      style={{
                        color: format === f.format ? "#c4b5fd" : "#9ca3af",
                      }}
                    >
                      {f.format}
                    </span>
                    {format === f.format && (
                      <div className="w-1.5 h-1.5 rounded-full bg-violet-400" />
                    )}
                  </div>
                  <p className="text-[10px] text-gray-700 leading-snug">
                    {formatDesc[f.format] || f.contentType}
                  </p>
                </button>
              ))}
            </div>
          </div>

          {/* Sample Rate */}
          <div className="space-y-2">
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-widest">
              Sample Rate
            </label>
            <div className="flex flex-wrap gap-2">
              {currentFormat?.validRates.map((rate) => (
                <button
                  key={rate}
                  onClick={() => setSampleRate(rate)}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
                  style={{
                    background:
                      sampleRate === rate
                        ? "rgba(139,92,246,0.15)"
                        : "rgba(255,255,255,0.04)",
                    border:
                      sampleRate === rate
                        ? "1px solid rgba(139,92,246,0.5)"
                        : "1px solid rgba(255,255,255,0.07)",
                    color: sampleRate === rate ? "#c4b5fd" : "#6b7280",
                  }}
                >
                  {rate / 1000}kHz
                  {rate === currentFormat.defaultRate && (
                    <span className="ml-1 text-[9px] opacity-60">rec</span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Summary card */}
          <div
            className="px-4 py-3.5 rounded-2xl space-y-2"
            style={{
              background: "rgba(139,92,246,0.07)",
              border: "1px solid rgba(139,92,246,0.15)",
            }}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Zap size={12} className="text-violet-500" />
                <span className="text-xs font-semibold text-gray-400">
                  Export Summary
                </span>
              </div>
              <span
                className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                style={{ background: `${qualityColor}18`, color: qualityColor }}
              >
                {qualityLevel} Quality
              </span>
            </div>
            <div className="grid grid-cols-2 gap-2 text-[11px]">
              <div>
                <p className="text-gray-600">Format</p>
                <p className="text-white font-semibold uppercase">{format}</p>
              </div>
              <div>
                <p className="text-gray-600">Sample Rate</p>
                <p className="text-white font-semibold">
                  {sampleRate / 1000} kHz
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div
          className="flex gap-3 px-6 pb-6"
          style={{
            borderTop: "1px solid rgba(255,255,255,0.06)",
            paddingTop: "1.25rem",
          }}
        >
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 rounded-xl text-sm font-medium text-gray-500 hover:text-white transition-all"
            style={{
              background: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.07)",
            }}
          >
            Cancel
          </button>
          <button
            onClick={() => onSave({ format, sampleRate })}
            className="flex-1 px-4 py-2.5 rounded-xl text-sm font-bold text-white transition-all"
            style={{
              background: "linear-gradient(135deg, #7c3aed, #6d28d9)",
              boxShadow: "0 4px 16px rgba(124,58,237,0.3)",
            }}
          >
            Save Settings
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProjectSettings;
